import React, { useState, useEffect, useMemo } from "react";
import log from "loglevel";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import ReactTooltip from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addAccountAction,
  modifyAccountAction,
  deleteAccountAction,
} from "store/actions/actionCreators";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { validAccountName, isStringValid } from "util/formValidators";
import AccountsTable from "components/dashboard/account/AccountsTable";
import {
  Account,
  CreateAccountRequestType,
  ModifyAccountRequestType,
  DeleteAccountRequestType,
} from "types";
import genStyles from "styles/General.module.scss";
import styles from "components/dashboard/Dashboard.module.scss";
import { Form } from "@avlino-inc/alenza-react";
import * as r from "routes";

type CreateAccountFormType = {
  name: string | undefined;
  description: string | undefined;
};

type ModifyAccountFormType = {
  name: string | undefined;
  description: string | undefined;
};

const AccountsCard = ({
  accounts,
  userIsAdmin,
  showCreateModal,
  setShowCreateModal,
  showSearch,
}: {
  accounts: Account[];
  userIsAdmin: boolean;
  showCreateModal: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSearch: boolean;
}) => {
  const dispatch = useDispatch();

  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const [createAccount, setCreateAccount] = useState<CreateAccountFormType>({
    name: undefined,
    description: undefined,
  });
  const [modifyAccount, setModifyAccount] = useState<ModifyAccountFormType>({
    name: undefined,
    description: undefined,
  });
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState<string>();
  // eslint-disable-next-line
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  // eslint-disable-next-line
  const [isModifyingAccount, setIsModifyingAccount] = useState(false);
  // eslint-disable-next-line
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  // const [showCreateModal, setShowCreateModal] = useState(showModal);

  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const onHandleCreateAccountClick = (event: any) => {
    event.preventDefault();
    if (
      createAccount?.name &&
      createAccount?.description &&
      validAccountName(createAccount?.name)
    ) {
      const request: CreateAccountRequestType = {
        body: {
          name: createAccount.name,
          description: createAccount.description,
        },
      };
      log.info("-- Dispatching loginUserAction()...");
      dispatch(addAccountAction(request));
      setIsCreatingAccount(false);
      setShowCreateModal(false);
      setCreateAccount({ name: undefined, description: undefined }); // clear
    }
  };

  const onHandleModifyAccountClick = (event: any) => {
    event.preventDefault();
    log.info("Attempting to modify Account...");
    if (
      selectedAccount &&
      selectedAccount.id &&
      modifyAccount?.name &&
      modifyAccount?.description &&
      validAccountName(modifyAccount?.name)
    ) {
      log.info("All fields valid! Modifying...");
      const request: ModifyAccountRequestType = {
        id: selectedAccount.id,
        body: {
          name: modifyAccount?.name,
          description: modifyAccount?.description,
        },
      };
      log.info("-- Dispatching modifyAccountAction()...");
      dispatch(modifyAccountAction(request));
      setIsModifyingAccount(false);
      setShowSettingsModal(false);
      setSelectedAccount(undefined); // clear
      setModifyAccount({ name: undefined, description: undefined }); // clear
    } else {
      log.info("One or more fields NOT VALID!");
    }
  };

  const confirmAccountDelete = () => {
    return selectedAccount && selectedAccount.name === deleteAccountConfirm;
  };

  const onHandleDeleteAccountClick = (event: any) => {
    event.preventDefault();
    if (selectedAccount && confirmAccountDelete()) {
      const request: DeleteAccountRequestType = {
        id: selectedAccount.id,
      };
      log.info("-- Dispatching deleteAccountAction()...");
      dispatch(deleteAccountAction(request));
      setIsDeletingAccount(false);
      setShowSettingsModal(false);
      setSelectedAccount(undefined); // clear
      setDeleteAccountConfirm(""); //clear
    }
  };

  const rowEvents = {
    onClick: (e: any, row: Account, rowIndex: number) => {
      log.info("Row Index: " + rowIndex);
      log.info("Row: " + JSON.stringify(row));
      setSelectedAccount(row);
      setModifyAccount({
        name: row.name,
        description: row.description,
      });
      setShowSettingsModal(true);
    },
  };

  const [wasNameTouched, setWasNameTouched] = useState(false);
  const [wasDescriptionTouched, setWasDescriptionTouched] = useState(false);

  const inputNameValidity = useMemo(() => {
    const isValid = isStringValid(createAccount?.name, validAccountName);
    const isInvalid = wasNameTouched && !isValid;
    if (isValid) return "valid";
    else if (isInvalid) return "invalid";
    else return undefined;
  }, [wasNameTouched, createAccount?.name]);
  const inputDescriptionValidity = useMemo(() => {
    const isValid = (createAccount?.description?.length || 0) > 0;
    const isInvalid = wasDescriptionTouched && !isValid;
    if (isValid) return "valid";
    else if (isInvalid) return "invalid";
    else return undefined;
  }, [wasDescriptionTouched, createAccount?.description]);

  const isFormValid = useMemo(
    () =>
      [inputNameValidity, inputDescriptionValidity].every(
        (value) => value === "valid"
      ),
    [inputNameValidity, inputDescriptionValidity]
  );

  const getValidity = () => {
    if (
      isStringValid(modifyAccount?.name, validAccountName) === true
    ) {
      return "valid";
    } else if (
      isStringValid(modifyAccount?.name, validAccountName) === false
    ) {
      return "invalid";
    }
  };

  const createModal = () => {
    return userIsAdmin ? (
      <>
        <Modal
          id="AccountCardCreateModal"
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setCreateAccount({ name: undefined, description: undefined });
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Create Account <FontAwesomeIcon icon={["fal", "folders"]} color="blue"/>
            </Modal.Title>
          </Modal.Header>
          <form name="create-account-form">
            <Modal.Body className={styles["modal__body"]}>
              <Form.Group controlId="account_name_create">
                <Form.Input
                  type="text"
                  placeholder="Account Name"
                  status={inputNameValidity}
                  onBlur={() => setWasNameTouched(true)}
                  onChange={(e: any) => {
                    if (e.target instanceof HTMLInputElement) {
                      const value = e.target.value;
                      setCreateAccount((state): typeof state => ({
                        ...state,
                        name: value,
                      }));
                    }
                  }}
                />
                <Form.Input.Feedback
                  renderAssistiveText={() =>
                    inputNameValidity === "invalid" ? (
                      <>
                        Name must be between 5-19 characters.
                        <br />
                        Name can only contain letters.
                      </>
                    ) : null
                  }
                />
              </Form.Group>

              <Form.Group controlId="account_description_create">
                <Form.Input
                  type="textarea"
                  rows={3}
                  columns={50}
                  maxLength={150}
                  placeholder="Please provide a brief description of this Account"
                  value={createAccount?.description}
                  style={{ resize: "none" }}
                  status={inputDescriptionValidity}
                  onBlur={() => setWasDescriptionTouched(true)}
                  onChange={(e: any) => {
                    if (e.target instanceof HTMLTextAreaElement) {
                      const value = e.target.value;
                      setCreateAccount((state) => ({
                        ...state,
                        description: value,
                      }));
                    }
                  }}
                />
                <Form.Input.Feedback
                  renderAssistiveText={() =>
                    inputDescriptionValidity === "invalid" ? (
                      <>Description cannot be blank.</>
                    ) : null
                  }
                />
              </Form.Group>
              <p>
                <small>{`${
                  150 -
                  (createAccount && createAccount.description
                    ? createAccount.description.length
                    : 0)
                } chars left`}</small>
              </p>
            </Modal.Body>
            <Modal.Footer>
              <input
                type="submit"
                value="Create"
                className={genStyles["alenza__btn-create__mini"]}
                disabled={!isFormValid}
                onClick={(e: any) => {
                  setIsCreatingAccount(true);
                  onHandleCreateAccountClick(e);
                }}
              />
            </Modal.Footer>
          </form>
        </Modal>
      </>
    ) : null;
  };

  const settingsModal = () => {
    return (
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showSettingsModal}
          onHide={() => {
            setShowSettingsModal(false);
            setModifyAccount({ name: undefined, description: undefined });
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Account Settings
              <FontAwesomeIcon
                style={{ marginLeft: "5px" }}
                icon={["fal", "cog"]}
              />
              {!userIsAdmin ? (
                <FontAwesomeIcon
                  style={{ marginLeft: "5px" }}
                  color="red"
                  icon={["fal", "lock-alt"]}
                  data-tip="View is restricted by role"
                  data-for="dashboard-tooltip"
                />
              ) : null}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              {selectedAccount ? (
                <Link to={r.ACCOUNT(selectedAccount.name.toLowerCase())}>
                  <button
                    type="submit"
                    className={genStyles["alenza__btn-primary__mini"]}
                  >
                    Go to {selectedAccount.name}
                    <FontAwesomeIcon icon={["fal", "long-arrow-right"]} />
                  </button>
                </Link>
              ) : null}
            </div>
          </Modal.Body>
          {userIsAdmin ? (
            <>
              <form name="modify-form">
                <Modal.Body>
                  <div>
                    <h5>Rename</h5>
                    <Form.Group controlId="account_name_edit">
                      <Form.Input
                        type="text"
                        placeholder="Account Name"
                        style={{ marginBottom: "30px" }}
                        value={modifyAccount?.name || ""}
                        status={getValidity()}
                        onBlur={() => setWasNameTouched(true)}
                        onChange={(e) => {
                          if (e.target instanceof HTMLInputElement) {
                            const value = e.target.value;
                            setModifyAccount((state) => ({
                              ...state,
                              name: value,
                            }));
                          }
                        }}
                      />
                    </Form.Group>

                    <h5>Edit Description</h5>
                    <Form.Group controlId="account_description_edit">
                      <Form.Input
                        rows={3}
                        columns={50}
                        maxLength={150}
                        placeholder="Please provide a brief description of this Account"
                        value={modifyAccount?.description}
                        style={{ resize: "none" }}
                        onChange={(e) => {
                          if (e.target instanceof HTMLTextAreaElement) {
                            const value = e.target.value;
                            setModifyAccount((state) => ({
                              ...state,
                              description: value,
                            }));
                          }
                        }}
                      />
                    </Form.Group>
                    <p>{`${
                      150 -
                      (modifyAccount && modifyAccount.description
                        ? modifyAccount.description.length
                        : 0)
                    } chars left`}</p>
                  </div>
                  <input
                    type="submit"
                    value="Save"
                    className={[
                      genStyles["alenza__btn-create__mini"],
                      "float-right",
                    ].join(" ")}
                    disabled={
                      !validAccountName(modifyAccount?.name) ||
                      (modifyAccount?.name === selectedAccount?.name && modifyAccount?.description === selectedAccount?.description)
                    }
                    onClick={(e: any) => {
                      setIsModifyingAccount(true);
                      onHandleModifyAccountClick(e);
                    }}
                  />
                </Modal.Body>
              </form>
              <hr />
              <form name="delete-form" style={{ marginBottom: "15px" }}>
                <Modal.Body>
                  <h5>Delete</h5>
                  <p>
                    Please confirm the name of the Account you want to delete.
                  </p>
                  <div>
                    <Form.Group controlId="account_delete">
                      <Form.Input
                        type="text"
                        placeholder="Confirm Account Name"
                        style={{ marginBottom: "25px" }}
                        value={deleteAccountConfirm || ""}
                        status={confirmAccountDelete() ? "valid" : "invalid"}
                        onBlur={() => setWasNameTouched(true)}
                        onChange={(e) => {
                          if (e.target instanceof HTMLInputElement) {
                            const value = e.target.value;
                            setDeleteAccountConfirm(value);
                          }
                        }}
                      />
                    </Form.Group>
                  </div>
                  <p>Are you sure you want to delete this Account?</p>
                  <p>
                    This action is <b>permanent and irreversible</b>.
                  </p>
                  <p>
                    All Analyses and Teams belonging to this Account will also
                    be deleted.
                  </p>
                  <input
                    type="submit"
                    value="Delete"
                    className={[
                      genStyles["alenza__btn-delete__mini"],
                      "float-right",
                    ].join(" ")}
                    disabled={!confirmAccountDelete()}
                    onClick={(e: any) => {
                      setIsDeletingAccount(true);
                      onHandleDeleteAccountClick(e);
                    }}
                  />
                </Modal.Body>
              </form>
            </>
          ) : (
            <>
              <Modal.Body>
                <h5>{selectedAccount?.name}</h5>
                <p style={{ fontStyle: "italic" }}>
                  {selectedAccount?.description}
                </p>
              </Modal.Body>
            </>
          )}
        </Modal>
        <ReactTooltip id="accounts-tooltip" />
      </>
    );
  };

  return (
    <>
      {accounts ? (
        <AccountsTable
          accounts={accounts}
          rowEvents={rowEvents}
          showSearch={showSearch}
        />
      ) : null}
      {createModal()}
      {settingsModal()}
    </>
  );
};

export default AccountsCard;
